import { ColorsStorage } from "../Constant/ColorsStorage";

type Color = {
  color: string;
  border: string;
  takenTimes: number;
};

export class Palette {
  palette: Map<string, Color>;
  colorCache: Map<string, Color>;

  constructor(customPalette?: string[]) {
    this.palette = new Map();
    this.colorCache = new Map();
    this.generateColorMap(customPalette);
  }

  public getSequentialColor(index: number): Color {
    const keys = Array.from(this.palette.keys());
    if (keys.length === 0) {
      throw new Error("Palette is empty");
    }
    const key = keys[index % keys.length];
    const color = this.palette.get(key ?? "");
    if (!color) {
      throw new Error(`No color found for key ${key}`);
    }
    return color;
  }

  private generateColorMap(customPalette?: string[]) {
    const colors = customPalette || ColorsStorage.color.fill;
    const borders = customPalette || ColorsStorage.color.border;

    for (let i = 0; i < colors.length; i++) {
      this.palette.set(i.toString(2).padStart(5, "0"), {
        color: colors[i]!,
        border: borders[i]!,
        takenTimes: 0,
      });
    }
  }

  public chooseColorMemo = (key: string) => {
    if (this.colorCache.has(key)) {
      return this.colorCache.get(key)!;
    }

    const color = this.chooseColor(key);
    this.colorCache.set(key, color);
    return color;
  };

  public chooseColor(str: string): Color {
    const hash = hashCode(str);
    const binary = toBinary(hash);
    const lastFive = binary.slice(-5);

    return this.getColor(lastFive);
  }

  public getColor(hash: string): Color {
    const [minTakenKey, minTakenColour] = Array.from(this.palette.entries()).reduce((a, b) => {
      return a[1].takenTimes < b[1].takenTimes ? a : b;
    });

    const colour = this.palette.get(hash);

    if (colour && colour.takenTimes === minTakenColour.takenTimes) {
      this.palette.set(hash, {
        ...colour,
        takenTimes: colour.takenTimes + 1,
      });

      return colour;
    }

    this.palette.set(minTakenKey, {
      ...minTakenColour,
      takenTimes: minTakenColour.takenTimes + 1,
    });

    return minTakenColour;
  }
}

export default Palette;

export const toBinary = (str: string) => {
  let result = "";
  for (let i = 0; i < str.length; i++) {
    const char = str.charAt(i);
    result += char.charCodeAt(0).toString(2);
  }
  return result;
};

export const hashCode = (str: string) => {
  let hash = 0;
  if (str.length === 0) {
    return hash.toString();
  }
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash.toString();
};
